import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Loader2, Building2 } from 'lucide-react';
import { CompanyCard } from '@/components/admin/CompanyCard';
import { fetchCompanies, fetchCompanyStats } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { toast } from 'sonner';

export default function CompanyManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tenant } = useTenant();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<any[]>([]);
  const [stats, setStats] = useState({ total: 0, activeUsers: 0, totalSims: 0, avgScore: 0 });

  // Use tenant from URL, not from user's profile
  const currentTenantId = tenant.tenant_id;
  const { isMasterTenant } = useTenant();
  
  // For tenant admins, show "Branches" instead of "Companies"
  const entityName = isMasterTenant ? 'Companies' : 'Branches';
  const entityNameSingular = isMasterTenant ? 'Company' : 'Branch';

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const companiesData = await fetchCompanies({ search: searchQuery, status: filterStatus, tenant_id: currentTenantId });
      // Pass tenant ID for white-label tenants to get tenant-specific stats
      const statsData = await fetchCompanyStats(isMasterTenant ? undefined : currentTenantId);
      setCompanies(companiesData);
      if (statsData) {
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, [searchQuery, filterStatus]);

  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      const matchesIndustry = filterIndustry === 'all' || company.industry === filterIndustry;
      return matchesIndustry;
    });
  }, [companies, filterIndustry]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading companies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-foreground mb-2 flex items-center gap-3">
              <Building2 className="h-9 w-9" />
              {entityName}
            </h1>
            <p className="text-lg text-muted-foreground">
              {isMasterTenant 
                ? 'Manage pharmaceutical companies and healthcare organizations'
                : `Manage branches and locations for ${tenant.tenant_name}`
              }
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/companies/create')}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-colors shadow-lg hover:shadow-xl"
          >
            <Plus size={20} />
            Add {entityNameSingular}
          </button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm text-muted-foreground mb-1">Total {entityName}</div>
            <div className="text-3xl font-bold text-foreground">{stats.total}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {isMasterTenant ? 'Active organizations' : 'Active branches'}
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm text-muted-foreground mb-1">Active Users</div>
            <div className="text-3xl font-bold text-green-600">{stats.activeUsers}</div>
            <div className="text-xs text-muted-foreground mt-1">Across all orgs</div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm text-muted-foreground mb-1">Total Simulations</div>
            <div className="text-3xl font-bold text-blue-600">{stats.totalSims}</div>
            <div className="text-xs text-muted-foreground mt-1">Platform-wide</div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm text-muted-foreground mb-1">Avg Team Score</div>
            <div className="text-3xl font-bold text-orange-600">{stats.avgScore}/100</div>
            <div className="text-xs text-muted-foreground mt-1">Platform average</div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                <input
                  type="text"
                  placeholder="Search companies by name or industry..."
                  className="w-full pl-10 pr-4 py-3 border border-input rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-transparent"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Industry Filter */}
            <select
              value={filterIndustry}
              onChange={(e) => setFilterIndustry(e.target.value)}
              className="px-4 py-2 border border-input rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Industries</option>
              <option value="pharmaceutical">Pharmaceutical</option>
              <option value="hospital">Hospital/Healthcare</option>
              <option value="research">Research Institution</option>
              <option value="medtech">Medical Technology</option>
            </select>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-input rounded-lg bg-background focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Companies Grid */}
        {filteredCompanies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompanies.map((company) => (
              <CompanyCard key={company.id} company={company} onDelete={loadCompanies} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg mb-4">No companies found</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setFilterIndustry('all');
                setFilterStatus('all');
              }}
              className="text-primary hover:underline"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
